<template>
  <div class="login-shell">
    <div class="card">
      <h1 class="title">
        Passwort vergessen
      </h1>
      <p class="subtitle">
        Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link zum Zurücksetzen Ihres
        Passworts.
      </p>

      <form
        class="form"
        @submit.prevent="submit"
      >
        <label
          class="label"
          for="email"
        >E‑Mail</label>
        <input
          id="email"
          v-model.trim="email"
          type="email"
          class="input"
          autocomplete="email"
          required
          placeholder="name@beispiel.de"
        >

        <button
          class="btn"
          :disabled="loading"
          data-testid="view-forgot-password"
        >
          <span
            v-if="loading"
            class="spinner"
            aria-hidden="true"
          />
          <span>{{ loading ? 'Wird gesendet...' : 'Link anfordern' }}</span>
        </button>

        <p
          v-if="message"
          class="notice notice-success mt-12"
          role="status"
        >
          {{ message }}
        </p>
        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>
      </form>

      <div class="actions">
        <router-link
          to="/login"
          class="back-link"
          data-testid="view-back-to-login"
        >
          Zurück zum Login
        </router-link>
      </div>
    </div>
    <PublicLegalFooter />
  </div>
</template>
<script setup>
import {ref} from 'vue';
import {requestPasswordReset} from '@/api/password';
import PublicLegalFooter from '@/components/layout/PublicLegalFooter.vue';

const email = ref('');
const loading = ref(false);
const message = ref('');
const error = ref('');

async function submit() {
  loading.value = true;
  error.value = '';
  message.value = '';
  try {
    await requestPasswordReset(email.value);
    message.value = 'Wenn die E-Mail existiert, wurde ein Reset-Link versendet.';
  } catch (e) {
    error.value = 'Es ist ein Fehler aufgetreten. Bitte später erneut versuchen.';
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.login-shell {
  min-height: 95vh;
  display: grid;
  place-items: center;
  background: linear-gradient(180deg, var(--bg, #F6F7F9) 0%, #EEF1F6 100%);
  padding: 24px;
}

.card {
  background: var(--surface, #FFFFFF);
  color: var(--text, #1F2937);
  width: 100%;
  max-width: 420px;
  border: 1px solid var(--border-strong, #E0E3EA);
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.08), 0 1px 0 rgba(0, 0, 0, .04);
  padding: 24px 20px;
}

.title {
  font-size: 20px;
  line-height: 24px;
  font-weight: 600;
  margin: 0 0 6px 0;
}

.subtitle {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted, #6B7280);
  margin: 0 0 16px 0;
}

.form {
  display: grid;
  gap: 14px;
}

.label {
  font-size: 12px;
  color: var(--muted, #6B7280);
}

.input {
  height: 40px;
  padding: 8px 10px;
  border: 1px solid var(--border-strong, #E0E3EA);
  border-radius: 8px;
  outline: none;
  background: #fff;
  color: var(--text, #1F2937);
  transition: border-color .12s ease, box-shadow .12s ease, background-color .12s ease;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
}

.input::placeholder {
  color: var(--muted, #6B7280);
  opacity: .9;
}

.input:hover {
  border-color: var(--primary-600, #4338CA);
}

.input:focus {
  border-color: var(--primary, #4F46E5);
  box-shadow: 0 0 0 3px var(--focus, rgba(79, 70, 229, .45));
}

.btn {
  height: 42px;
  border-radius: 8px;
  background: var(--primary, #4F46E5);
  color: #fff;
  border: 1px solid var(--primary, #4F46E5);
  cursor: pointer;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color .12s ease, box-shadow .12s ease, transform .02s ease-in;
  box-shadow: 0 1px 0 rgba(0, 0, 0, .04), 0 6px 16px rgba(79, 70, 229, .18);
}

.btn:hover {
  background: var(--primary-600, #4338CA);
}

.btn:active {
  transform: translateY(1px);
}

.btn:disabled {
  opacity: .7;
  cursor: default;
  box-shadow: none;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, .55);
  border-top-color: #fff;
  border-radius: 50%;
  display: inline-block;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.actions {
  margin-top: 20px;
  text-align: center;
}

.back-link {
  font-size: 14px;
  color: var(--primary, #4F46E5);
  text-decoration: none;
  transition: color .12s ease, text-decoration .12s ease;
}

.back-link:hover {
  text-decoration: underline;
}
</style>